import { Platform } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";

/**
 * The width of the app scene a screen is laid out in, when it is known WITHOUT measuring —
 * or `null` when it is not.
 *
 * A screen that knows its width up front can size its content on the first commit (a grid can
 * hand every tile a precomputed glyph size instead of letting each tile measure itself via
 * `onLayout`, a per-tile native round-trip plus a second render). The width is deterministic in
 * the case that matters most — a native phone in portrait — where the app shell is the
 * bottom-tabs layout: the scene spans the full window and carries no horizontal safe-area inset.
 *
 * Every other shell is reported as unknown rather than approximated:
 * - native LANDSCAPE mounts the sidebar drawer beside the scene (see `useUsesSidebarLayout` /
 *   `AppShell`) and can carry notch insets on either side;
 * - web renders the scene straight through a `<Slot />` under the page header, whose column
 *   width is a CSS concern this hook does not model.
 * A `null` tells the caller to fall back to its `onLayout` measurement, which is always correct.
 */
export function useNavContentWidth(): number | null {
  const [windowDimensions] = useContextSelectors(RootContext, (c) => c.windowDimensions);
  if (Platform.OS === "web") {
    return null;
  }
  // The same test `useUsesSidebarLayout` applies: landscape swaps the tabs for the sidebar.
  if (windowDimensions.width > windowDimensions.height) {
    return null;
  }
  return windowDimensions.width;
}
